import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

interface AdjustAdvanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: string;
  invoice: any;
  availableAdvance: number;
}

export default function AdjustAdvanceModal({ isOpen, onClose, customer, invoice, availableAdvance }: AdjustAdvanceModalProps) {
  useEscapeToClose(onClose, isOpen);
  const queryClient = useQueryClient();
  const maxAmount = Math.min(availableAdvance, invoice ? Number(invoice.balance_amount) : 0);
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (maxAmount > 0) {
        setAmount(String(maxAmount));
      } else {
        setAmount('');
      }
    } else {
      setAmount('');
    }
  }, [isOpen, maxAmount]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!invoice) throw new Error('Invoice is missing');
      const numAmount = Number(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        throw new Error('Please enter a valid amount');
      }
      if (numAmount > maxAmount + 0.0001) {
        throw new Error(`Amount cannot exceed Rs. ${maxAmount.toLocaleString()}`);
      }
      
      const payload = {
        invoice_name: invoice.name,
        amount: numAmount
      };
      
      await api.post('/method/saas_pos.api.sales.apply_advance_to_invoice', payload);
    },
    onSuccess: () => {
      toast.success('Advance adjusted successfully');
      queryClient.invalidateQueries({ queryKey: ['customerInvoices', customer] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setAmount('');
      onClose();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'Failed to adjust advance');
    },
  });

  if (!isOpen || !invoice) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-900">Adjust Advance</h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <p className="text-sm text-gray-600">
            Applying advance to invoice <span className="font-medium text-gray-900">{invoice.name}</span>
          </p>
          <div className="flex gap-4">
            <div className="flex-1 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-xs text-amber-800/70 mb-1">Available Advance</p>
              <p className="text-sm font-semibold text-amber-800">Rs. {availableAdvance.toLocaleString()}</p>
            </div>
            <div className="flex-1 p-3 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-xs text-red-800/70 mb-1">Invoice Balance</p>
              <p className="text-sm font-semibold text-red-800">Rs. {Number(invoice.balance_amount).toLocaleString()}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount to Apply (Rs) *</label>
            <input
              type="number"
              min="0"
              max={maxAmount}
              step="any"
              value={amount}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (e.target.value !== '' && val > maxAmount) setAmount(String(maxAmount));
                else setAmount(e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Maximum allowed: Rs. {maxAmount.toLocaleString()}</p>
          </div>
        </div>

        <div className="p-5 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !amount || Number(amount) <= 0}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {saveMutation.isPending ? 'Saving...' : 'Apply Advance'}
          </button>
        </div>
      </div>
    </div>
  );
}
